import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ErrorMessage, Loading } from '@Components';
import { useFetchData } from '@CustomHooks';
import { ClientConfiguration, ClientMaster, Gs1ConfigurationLink } from '@Models';
import { bopsApi } from '@Network';
import { RootState } from '@Store';
import config from '../../config';
import Configure from './configureLink';

/**
 * Where this deployment's masters come from.
 *
 * Declared outside the component, and branching inside rather than
 * picking between two functions: the two listings answer with different
 * shapes, and a conditional that returns one or the other hides that
 * from everything downstream.
 */
const fetchConfigurations = (): Promise<Gs1ConfigurationLink[] | ClientMaster[]> =>
  config.isGs1Flavor ? bopsApi.getConfigurationLinksGS1() : bopsApi.getMasters();

const ConfigureContainer = () => {
  const { t } = useTranslation();
  const clientConfiguration = useSelector<RootState, ClientConfiguration | undefined>(
    (state) => state.client?.configuration,
  );
  const readOnlyMasters = clientConfiguration?.readOnlyMasters ?? false;

  const { data, isLoading: isLoadingLinks, error, refetch } = useFetchData(fetchConfigurations);

  // Always called, skipped where it has no answer to give: a hook behind
  // a condition changes how many run between renders, which React reads
  // as a different component. The sources only name a new master, so a
  // deployment that does not create them has nothing to ask for.
  const { data: sources, isLoading: isLoadingSources } = useFetchData(
    bopsApi.getSources,
    undefined,
    !config.createsMasters,
  );

  // A skipped fetch never reports finishing, so it is not waited on.
  const isLoading = isLoadingLinks || (config.createsMasters && isLoadingSources);

  if (isLoading) {
    return <Loading title={t('Configuration.LoadingLink')} />;
  }

  if (error) {
    return <ErrorMessage internalMessage={`Unable to get configuration links: ${String(error)}`} />;
  }

  return (
    <Configure
      configurationLinks={data || []}
      refetch={refetch}
      sources={sources || []}
      readOnlyMasters={readOnlyMasters}
    />
  );
};

export default ConfigureContainer;
